/**
 * FinishedLot + StockMovement schemas.
 *
 * Invariants enforced here:
 *  - FinishedLot.lot_number is server-generated on OT close (never manual): read shape
 *    only; there is no client "create finished lot with lot number" input.
 *  - StockMovement is append-only; a `reason` is MANDATORY for adjustment / write_off /
 *    reversal (the mandatory-reason invariant). Reversals reference the reversed movement.
 */

import { z } from 'zod';
import {
  FINISHED_LOT_STATUSES,
  MOVEMENT_TYPES,
  MOVEMENT_REASONS,
  STOCK_MANUAL_STATES,
  STOCK_REGULARIZE_KINDS,
} from '../enums/index.js';
import { STOCK_ADJUST_REASONS } from '../domain/stockAdjust.js';
import { MOVEMENT_TYPES_REQUIRING_REASON } from '../constants/index.js';
import {
  uuid,
  nonEmptyString,
  timestamptz,
  dateString,
  quantity,
  nonNegativeQuantity,
  syncFields,
  timestampFields,
} from './common.js';

// ── FinishedLot ───────────────────────────────────────────────────────────────

/**
 * Finished/intermediate lot, generated automatically on OT close. `lot_number` is
 * server-generated (never manual) and `expiry_date` is suggested from the article shelf
 * life. Read shape only — creation happens via WorkOrderCloseInput on the OT-close path.
 */
export const FinishedLot = z
  .object({
    id: uuid,
    tenant_id: uuid,
    lot_number: nonEmptyString,
    work_order_id: uuid,
    article_id: uuid,
    recipe_version_id: uuid,
    production_date: dateString,
    expiry_date: dateString.nullable(),
    produced_quantity: nonNegativeQuantity,
    unit_id: uuid,
    /** Where the lot lives now (Zona / Secció / Ubicació) — see placeFields. */
    zone_id: uuid.nullable(),
    section_id: uuid.nullable(),
    location_id: uuid.nullable(),
    status: z.enum(FINISHED_LOT_STATUSES),
    handling_unit_id: uuid.nullable(),
    ...timestampFields,
  })
  .strict();
export type FinishedLot = z.infer<typeof FinishedLot>;

// ── StockMovement (append-only) ───────────────────────────────────────────────

const reasonRequiringTypes = new Set<string>(MOVEMENT_TYPES_REQUIRING_REASON);

export const StockMovement = z
  .object({
    id: uuid,
    tenant_id: uuid,
    article_id: uuid,
    movement_type: z.enum(MOVEMENT_TYPES),
    quantity: quantity, // signed (+in / -out)
    unit_id: uuid,
    supplier_lot_id: uuid.nullable(),
    finished_lot_id: uuid.nullable(),
    // The place the movement happened (Zona / Secció / Ubicació). When a client
    // sends none, the server derives it (the lot's place, the reversed movement's,
    // or the article's default) — see 20260925130000_estructura-obrador.sql.
    zone_id: uuid.nullable(),
    section_id: uuid.nullable(),
    location_id: uuid.nullable(),
    /** Pairs the two movements (−origin / +destination) of one traspàs. */
    transfer_id: uuid.nullable(),
    work_order_id: uuid.nullable(),
    reception_id: uuid.nullable(),
    reason: z.enum(MOVEMENT_REASONS).nullable(),
    reason_note: z.string().trim().min(1).nullable(),
    reverses_movement_id: uuid.nullable(),
    handling_unit_id: uuid.nullable(),
    recorded_by: uuid,
    recorded_at: timestamptz,
    ...syncFields,
    created_at: timestamptz,
  })
  .strict()
  .refine(
    (m) => !reasonRequiringTypes.has(m.movement_type) || m.reason !== null,
    {
      message:
        'reason is mandatory for adjustment / write_off / reversal movements',
      path: ['reason'],
    },
  )
  .refine(
    (m) => m.movement_type !== 'reversal' || m.reverses_movement_id !== null,
    {
      message: 'a reversal movement must reference reverses_movement_id',
      path: ['reverses_movement_id'],
    },
  )
  .refine((m) => (m.movement_type === 'transfer') === (m.transfer_id !== null), {
    message: 'a transfer movement (and only a transfer) carries transfer_id',
    path: ['transfer_id'],
  });
export type StockMovement = z.infer<typeof StockMovement>;

/**
 * Manual stock adjustment / write-off / reversal create payload. A written reason is
 * MANDATORY (corrections are documented counter-movements, never edits). Reception,
 * consumption and production_output movements are created by their own flows, so this
 * input only accepts the reason-requiring types.
 */
export const StockMovementAdjustInput = z
  .object({
    article_id: uuid,
    movement_type: z.enum(['adjustment', 'write_off', 'reversal']),
    quantity: quantity, // signed
    unit_id: uuid,
    supplier_lot_id: uuid.optional(),
    finished_lot_id: uuid.optional(),
    zone_id: uuid.optional(),
    reason: z.enum(MOVEMENT_REASONS),
    reason_note: z.string().trim().min(1).optional(),
    reverses_movement_id: uuid.optional(),
    recorded_at: timestamptz,
    client_uuid: uuid.optional(),
  })
  .strict()
  .refine(
    (m) => m.movement_type !== 'reversal' || m.reverses_movement_id !== undefined,
    {
      message: 'a reversal must reference reverses_movement_id',
      path: ['reverses_movement_id'],
    },
  )
  // A manual ADJUSTMENT requires a written reason note (the mandatory-reason
  // invariant), enforced here so it holds server-side too — not just in the UI.
  .refine((m) => m.movement_type !== 'adjustment' || (m.reason_note?.length ?? 0) > 0, {
    message: 'an adjustment requires a written reason note',
    path: ['reason_note'],
  });
export type StockMovementAdjustInput = z.infer<typeof StockMovementAdjustInput>;

// ── Traspàs (move_stock) ─────────────────────────────────────────────────────────

/**
 * A place of the obrador's structure as stock_balances reports it. All three null
 * is a real place: «sense lloc», where the stock received before the structure
 * existed still sits until someone moves it.
 */
export const StockPlace = z
  .object({
    zone_id: uuid.nullable(),
    section_id: uuid.nullable(),
    location_id: uuid.nullable(),
  })
  .strict();
export type StockPlace = z.infer<typeof StockPlace>;

/**
 * Move `quantity` (article BASE unit) of one lot — or of an article's unlotted
 * stock — from one place to another. The server (move_stock RPC) writes the two
 * paired `transfer` movements; the destination must be a real place (a zone at
 * least) and different from the origin. No reason: a move corrects nothing, it
 * only says where the goods are. An optional note travels with both movements.
 */
export const StockTransferInput = z
  .object({
    article_id: uuid,
    supplier_lot_id: uuid.nullable(),
    finished_lot_id: uuid.nullable(),
    from: StockPlace,
    to: StockPlace,
    quantity: quantity.refine((q) => q > 0, { message: 'must be positive' }),
    note: nonEmptyString.optional(),
  })
  .strict()
  .refine((m) => !(m.supplier_lot_id !== null && m.finished_lot_id !== null), {
    message: 'a movement belongs to one lot at most',
    path: ['finished_lot_id'],
  })
  .refine((m) => m.to.zone_id !== null, {
    message: 'the destination needs at least a zone',
    path: ['to', 'zone_id'],
  })
  .refine(
    (m) =>
      m.from.zone_id !== m.to.zone_id ||
      m.from.section_id !== m.to.section_id ||
      m.from.location_id !== m.to.location_id,
    { message: 'origin and destination are the same place', path: ['to'] },
  );
export type StockTransferInput = z.infer<typeof StockTransferInput>;

// ── Ajust per Lot + Lloc (adjust_stock, OBR-143) ───────────────────────────────────────────

const lotRefs = {
  supplier_lot_id: uuid.nullable(),
  finished_lot_id: uuid.nullable(),
};

/** El lloc d'una línia d'existències: tot NUL és «sense lloc». */
const placeRefs = {
  zone_id: uuid.nullable(),
  section_id: uuid.nullable(),
  location_id: uuid.nullable(),
};

/**
 * Ajust (+/−) d'UN lot en UN lloc, en UNITAT BASE de l'article. El servidor (RPC `adjust_stock`) el fa complir: motiu i nota
 * escrits obligatoris, mai un saldo negatiu, mai estoc nou sense lot ni —quan l'article la demana— sense caducitat. Un
 * ajust sense lot només treu (o salda un negatiu sense lot fins a zero). `expiry_date` només es fa servir per posar la
 * caducitat a un lot que no en té: si ja en té, no se'n canvia.
 *
 * `client_uuid` (opcional, `p_client_uuid` a l'RPC) fa el reintent IDEMPOTENT: una crida que arriba dues vegades (un 504 amb
 * commit i el reintent del BackOffice) no escriu dos ajustos, sinó que torna l'id del primer. S'ha de generar UN cop per obertura
 * del diàleg (no per clic) i reutilitzar-lo en cada reintent, amb EXACTAMENT els mateixos paràmetres: el servidor desa l'empremta
 * de la petició (article, lot, lloc, quantitat, motiu, nota i caducitat) i un uuid ja usat amb qualsevol altra cosa, o per un altre
 * moviment, dona `adjust_stock_client_uuid_conflict` (mai un èxit amb un ajust que no s'ha fet). Si l'usuari canvia res al
 * diàleg, cal un uuid nou.
 */
export const StockAdjustInput = z
  .object({
    article_id: uuid,
    ...lotRefs,
    ...placeRefs,
    quantity: quantity.refine((q) => q !== 0, { message: 'an adjustment cannot be zero' }),
    reason: z.enum(STOCK_ADJUST_REASONS),
    note: nonEmptyString,
    expiry_date: dateString.nullable().optional(),
    client_uuid: uuid.nullable().optional(),
  })
  .strict()
  .refine((m) => !(m.supplier_lot_id !== null && m.finished_lot_id !== null), {
    message: 'a movement belongs to one lot at most',
    path: ['finished_lot_id'],
  });
export type StockAdjustInput = z.infer<typeof StockAdjustInput>;

/** Els arguments de l'RPC `adjust_stock`, amb els noms exactes de la funció (`p_…`). */
export function adjustStockRpcArgs(input: StockAdjustInput): Record<string, unknown> {
  return {
    p_article_id: input.article_id,
    p_supplier_lot_id: input.supplier_lot_id,
    p_finished_lot_id: input.finished_lot_id,
    p_zone_id: input.zone_id,
    p_section_id: input.section_id,
    p_location_id: input.location_id,
    p_quantity: input.quantity,
    p_reason: input.reason,
    p_note: input.note,
    p_expiry_date: input.expiry_date ?? null,
    p_client_uuid: input.client_uuid ?? null,
  };
}

/**
 * Lot nou d'ajust (RPC `create_adjustment_lot`): un lot de proveïdor intern, de número generat pel servidor, amb la seva
 * quantitat (més gran que zero, unitat base) i la caducitat (obligatòria quan l'article la demana: el servidor ho comprova).
 * Només per a articles amb lots de proveïdor; un de només de producció dona `lot_requires_work_order`.
 * `client_uuid`: com a {@link StockAdjustInput}, un reintent amb el mateix uuid torna el MATEIX lot, sense crear-ne un altre.
 */
export const AdjustmentLotInput = z
  .object({
    article_id: uuid,
    supplier_lot_code: nonEmptyString.nullable().optional(),
    expiry_date: dateString.nullable(),
    ...placeRefs,
    quantity: quantity.refine((q) => q > 0, { message: 'must be positive' }),
    reason: z.enum(STOCK_ADJUST_REASONS),
    note: nonEmptyString,
    client_uuid: uuid.nullable().optional(),
  })
  .strict();
export type AdjustmentLotInput = z.infer<typeof AdjustmentLotInput>;

/** Els arguments de l'RPC `create_adjustment_lot`, amb els noms exactes de la funció (`p_…`). */
export function adjustmentLotRpcArgs(input: AdjustmentLotInput): Record<string, unknown> {
  return {
    p_article_id: input.article_id,
    p_supplier_lot_code: input.supplier_lot_code ?? null,
    p_expiry_date: input.expiry_date,
    p_zone_id: input.zone_id,
    p_section_id: input.section_id,
    p_location_id: input.location_id,
    p_quantity: input.quantity,
    p_reason: input.reason,
    p_note: input.note,
    p_client_uuid: input.client_uuid ?? null,
  };
}

// ── Estats d'un lot en un lloc (set_stock_state, OBR-14) ────────────────────────────────────

/**
 * Bloqueja o desbloqueja UN lot en UN lloc (RPC `set_stock_state`). El motiu és OPCIONAL («motiu si s'informa»). Només dos
 * estats manuals; caducat i caduca aviat es deriven de la data.
 */
export const StockStateChangeInput = z
  .object({
    article_id: uuid,
    ...lotRefs,
    ...placeRefs,
    state: z.enum(STOCK_MANUAL_STATES),
    reason: nonEmptyString.nullable().optional(),
  })
  .strict()
  .refine((m) => (m.supplier_lot_id === null) !== (m.finished_lot_id === null), {
    message: 'a state change belongs to exactly one lot',
    path: ['supplier_lot_id'],
  });
export type StockStateChangeInput = z.infer<typeof StockStateChangeInput>;

/** Els arguments de l'RPC `set_stock_state`, amb els noms exactes de la funció (`p_…`). */
export function setStockStateRpcArgs(input: StockStateChangeInput): Record<string, unknown> {
  return {
    p_article_id: input.article_id,
    p_supplier_lot_id: input.supplier_lot_id,
    p_finished_lot_id: input.finished_lot_id,
    p_zone_id: input.zone_id,
    p_section_id: input.section_id,
    p_location_id: input.location_id,
    p_state: input.state,
    p_reason: input.reason ?? null,
  };
}

/** Un canvi d'estat tal com el desa `stock_state_changes` (historial immutable). */
export const StockStateChange = z
  .object({
    id: uuid,
    tenant_id: uuid,
    article_id: uuid,
    ...lotRefs,
    ...placeRefs,
    state: z.enum(STOCK_MANUAL_STATES),
    reason: z.string().trim().min(1).nullable(),
    changed_by: uuid,
    changed_at: timestamptz,
  })
  .strict();
export type StockStateChange = z.infer<typeof StockStateChange>;

/** L'estat VIGENT d'un lot en un lloc (vista `stock_lot_place_states`: l'últim canvi de cada lot + lloc). */
export const StockLotPlaceState = z
  .object({
    tenant_id: uuid,
    article_id: uuid,
    ...lotRefs,
    ...placeRefs,
    state: z.enum(STOCK_MANUAL_STATES),
    reason: z.string().trim().min(1).nullable(),
    changed_by: uuid,
    changed_at: timestamptz,
  })
  .strict();
export type StockLotPlaceState = z.infer<typeof StockLotPlaceState>;

// ── «A regularitzar» (vista stock_to_regularize, OBR-143) ───────────────────────────────────

/**
 * Una línia de l'informe «A regularitzar»: un lot + lloc (o l'estoc sense lot d'un lloc) amb un problema (`kind`). Una mateixa
 * línia pot sortir amb dos tipus. `quantity` és el saldo de la línia en unitat base (negatiu als saldos en negatiu).
 */
export const StockToRegularizeRow = z
  .object({
    tenant_id: uuid,
    article_id: uuid,
    ...lotRefs,
    ...placeRefs,
    quantity: quantity,
    kind: z.enum(STOCK_REGULARIZE_KINDS),
  })
  .strict();
export type StockToRegularizeRow = z.infer<typeof StockToRegularizeRow>;
